import type { ObservableMap } from 'mobx'
import { observable } from 'mobx'
import { BroadcastState, type TranscriptMessage } from '../../types'
import type { MeetingCubit } from '../MeetingCubit'
import { SlideType } from '../../firestore/Slide/types'

// How long the slide timer will stay in its loading state waiting for the clock
// offset to be measured before drawing anyway, so it can never hang if a
// measurement never completes (e.g. the auth token fetch fails).
const CLOCK_OFFSET_WAIT_MS = 5_000

export class SlideTimerController {
  meeting: MeetingCubit
  _messages: ObservableMap<number, TranscriptMessage> = observable.map({})

  slideTimer: NodeJS.Timeout | null = null
  videoDidNotStartTimer: NodeJS.Timeout | null = null
  _clockOffsetWaitStartedAt: number | null = null
  debugMode = false

  constructor(meeting: MeetingCubit, options: { debug?: boolean } = {}) {
    this.meeting = meeting
    this.debugMode = options.debug || false
  }

  dispose() {
    this.cancelSlideTimer()
    this.cancelVideoDidNotStartTimer()
  }

  cancelSlideTimer() {
    this.debugLog('cancelSlideTimer')
    if (this.slideTimer) {
      clearInterval(this.slideTimer)
    }
  }

  cancelVideoDidNotStartTimer() {
    this.debugLog('cancelVideoDidNotStartTimer')
    if (this.videoDidNotStartTimer) {
      clearTimeout(this.videoDidNotStartTimer)
    }
  }

  resetSlideTimer() {
    this.debugLog('resetSlideTimer')
    this.meeting.updateProgressBar(0)
    this.cancelSlideTimer()
    this.cancelVideoDidNotStartTimer()

    if (
      this.meeting.currentSlide?.type !== SlideType.video &&
      this.meeting.currentSlide?.duration === 0
    ) {
      this.meeting.updateProgressBar(1)
    } else {
      this.calculateSlideTimer()
      this.slideTimer = setInterval(() => {
        this.calculateSlideTimer()
      }, 1000)
    }

    this.startVideoDidNotStartTimer()
  }

  startVideoDidNotStartTimer() {
    // YouTube embeds have no LiveKit stream to kick into playing.
    const isVideo =
      this.meeting.currentSlide?.type === SlideType.video &&
      !this.meeting.currentSlide?.isYouTubeSlide
    const isBroadcasting = !this.meeting.streamingMode

    if (isVideo && isBroadcasting) {
      this.videoDidNotStartTimer = setTimeout(() => {
        if (this.meeting.breakoutParticipantIsPresent) {
          if (this.meeting.broadcastPosition === 0) {
            this.meeting.updateProgressBar(1)
            this.meeting.sendBroadcastMessage('play')
          }
        } else {
          this.meeting.updateProgressBar(1)
          this.meeting.setBroadcastState(BroadcastState.stopped)
        }
      }, 8000)
    }
  }

  calculateSlideTimer() {
    this.debugLog('calculateSlideTimer')
    const currentSlide = this.meeting.currentSlide

    // Every video slide drives its own progress from the real player position:
    // uploaded/broadcast videos via LiveKit stream status, and YouTube embeds via
    // the IFrame Player API (BL-4662). Approximating YouTube progress from a
    // wall-clock timer here desynced the Next-button gate from what the viewer
    // saw — buffering, a paused/rewatched video, or a late joiner bumping
    // activeSlideChangedAt could leave Next stuck disabled after the video ended.
    const isVideoSlide = currentSlide?.type === SlideType.video
    // Dynamic Intro Video advances when its video ends (handled by the
    // renderer), so — like a video slide — the timer must not auto-advance it.
    const slideControlsProgress =
      isVideoSlide || currentSlide?.type === SlideType.dynamicIntroVideo

    if (slideControlsProgress) return

    const slideDuration = currentSlide?.duration
    const slideChangedAt = this.meeting.activeSlideChangedAt
    if (!slideChangedAt || !slideDuration) {
      this.debugLog('slideChangedAt or slideDuration is not set', {
        slideChangedAt,
        slideDuration,
      })
      return
    }

    // On rejoin the clock offset may not be measured yet, or a measurement may
    // still be in flight (a large offset waits on a second token round trip).
    // Drawing now would flash a countdown computed from a skewed device clock.
    // Stay in the loading state (leave the progress bar untouched) until the
    // measurement finishes, but no longer than a short grace window, so the
    // timer never hangs if it never completes. The next tick recomputes.
    const repository = this.meeting.repository
    if (!repository.clockOffsetMeasured || !repository.clockOffsetSettled) {
      this._clockOffsetWaitStartedAt ??= Date.now()
      if (Date.now() - this._clockOffsetWaitStartedAt < CLOCK_OFFSET_WAIT_MS) {
        this.debugLog('clock offset pending; deferring slide-timer draw')
        return
      }
    } else {
      this._clockOffsetWaitStartedAt = null
    }

    // serverNow() corrects for device clock skew: slideChangedAt is a server
    // timestamp, so elapsed must be measured against the server timeline.
    const elapsedSeconds =
      (repository.serverNow() - slideChangedAt.getTime()) / 1000
    const slideExpired = elapsedSeconds >= slideDuration

    // Past the grace window on an uncorrected clock that runs behind, elapsed
    // can be negative. Clamp so the bar never shows more than the full slide.
    const currentProgress = Math.max(elapsedSeconds / slideDuration, 0)

    const timeDifference = elapsedSeconds % 5

    /// If elapsed time is less than slide duration + 1 minute
    /// or there is an integer of 5 seconds since the last time
    /// we updated the progress bar
    if (!slideExpired || timeDifference === 0) {
      this.meeting.updateProgressBar(
        currentProgress === 0 ? 0.0001 : currentProgress
      )
    } else {
      this.debugLog('skipping progress update')
    }

    // endTimer cancels the interval, so only end on a verified clock. Without
    // an applied offset serverNow() is the raw device clock, and the slide may
    // read as expired only because that clock runs ahead: keep ticking so the
    // timer can redraw the real remaining time once an offset lands.
    if (slideExpired) {
      if (repository.clockOffsetVerified && repository.clockOffsetSettled) {
        this.endTimer()
      } else {
        this.debugLog('clock offset not verified; slide not ended yet')
      }
    }

    this.debugLog({
      slideDuration,
      elapsedSeconds,
      timeDifference,
    })
  }

  endTimer() {
    this.cancelSlideTimer()
    this.cancelVideoDidNotStartTimer()
    this.meeting.updateProgressBar(1)
  }

  debugLog(...args: unknown[]) {
    if (this.debugMode) {
      // eslint-disable-next-line no-console
      console.log(...args)
    }
  }
}
